import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import API_BASE_URL from "../../utils/config";
import CustomerSearchSelect from "./CustomerSearchSelect";

const emptyBlock = {
  customerId: "",
  customerType: "",
  commissionType: "all_products",
  commissionValue: "",
  selectedProducts: [],
};

const DoctorForm = () => {
  const token = localStorage.getItem("token");
  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const linkToCustomer = searchParams.get("linkToCustomer");
  const redirect = searchParams.get("redirect");

  const [form, setForm] = useState({
    name: "",
    mobile: "",
    specialization: "",
    email: "",
    address: "",
    license_no: "",
    customerCommissions: [emptyBlock],
  });

  const [products, setProducts] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);

  // ------------------ FETCHES ------------------

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/products`, authHeader);
      setProducts(Array.isArray(res.data) ? res.data : res.data?.rows || []);
    } catch {
      setProducts([]);
    }
  };

  const fetchDoctor = async () => {
    if (!id) {
      setLoading(false);
      return;
    }

    try {
      setIsEditMode(true);
      const res = await axios.get(`${API_BASE_URL}/api/doctors/${id}`, authHeader);
const { doctor, customerCommissions } = res.data;

setForm({
  name: doctor.name || "",
  mobile: doctor.mobile || "",
  specialization: doctor.specialization || "",
  email: doctor.email || "",
  address: doctor.address || "",
  license_no: doctor.license_no || "",
  customerCommissions:
    customerCommissions?.length
      ? customerCommissions
      : [emptyBlock],
});

    } catch (err) {
      console.error("Error loading doctor", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchDoctor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Preselect customer if launched from customer page
  useEffect(() => {
    if (!linkToCustomer) return;

    setForm((prev) => {
      const blocks = [...prev.customerCommissions];
      if (!blocks[0]?.customerId) {
        blocks[0] = { ...blocks[0], customerId: String(linkToCustomer) };
      }
      return { ...prev, customerCommissions: blocks };
    });
  }, [linkToCustomer]);

  // ------------------ HANDLERS ------------------

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const updateBlock = (index, field, value) => {
    setForm((prev) => {
      const updated = [...prev.customerCommissions];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, customerCommissions: updated };
    });
  };

  const toggleProduct = (index, productId) => {
    setForm((prev) => {
      const updated = [...prev.customerCommissions];
      const cur = updated[index].selectedProducts || [];
      updated[index].selectedProducts = cur.includes(productId)
        ? cur.filter((id) => id !== productId)
        : [...cur, productId];
      return { ...prev, customerCommissions: updated };
    });
  };

  const addBlock = () => {
    setForm((prev) => ({
      ...prev,
      customerCommissions: [...prev.customerCommissions, emptyBlock],
    }));
  };

  const removeBlock = (index) => {
    setForm((prev) => ({
      ...prev,
      customerCommissions: prev.customerCommissions.filter(
        (_, i) => i !== index
      ),
    }));
  };

  // ------------------ SUBMIT ------------------

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);

      if (isEditMode) {
        await axios.put(`${API_BASE_URL}/api/doctors/${id}`, form, authHeader);
        alert("Doctor updated");
        navigate("/doctors");
      } else {
        await axios.post(`${API_BASE_URL}/api/doctors`, form, authHeader);

        const firstCustomer =
          linkToCustomer ||
          form.customerCommissions.map((c) => c.customerId).find(Boolean);

        navigate(firstCustomer ? redirect || `/customers/${firstCustomer}` : "/doctors");
      }
    } catch (err) {
      console.error("Save error", err);
      alert("Failed to save doctor");
    } finally {
      setSaving(false);
    }
  };

  // ------------------ UI ------------------

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6 bg-white shadow rounded">
      <h2 className="text-xl font-bold mb-4">
        {isEditMode ? "Edit Doctor" : "Add Doctor"}
      </h2>

      <form onSubmit={handleSubmit}>
        {/* Doctor fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {["name", "mobile", "specialization", "email", "address", "license_no"].map((f) => (
            <input
              key={f}
              name={f}
              placeholder={f.replace("_", " ").toUpperCase()}
              value={form[f]}
              onChange={handleChange}
              className="border p-2 rounded"
              required={f === "name"}
            />
          ))}
        </div>

        <h3 className="text-lg font-semibold mt-6 mb-3">
          Customer Commission Mapping
        </h3>

        {form.customerCommissions.map((cc, index) => (
          <div key={index} className="border p-4 rounded mb-4 bg-gray-50">
            <CustomerSearchSelect
              token={token}
              value={cc.customerId}
              onChange={(v) => updateBlock(index, "customerId", v)}
            />

            <select
              value={cc.commissionType}
              onChange={(e) =>
                updateBlock(index, "commissionType", e.target.value)
              }
              className="border p-2 rounded w-full mt-2"
            >
              <option value="all_products">All Products</option>
              <option value="product_specific">Selected Products</option>
              <option value="excluded_products">Exclude Products</option>
            </select>

            <input
              type="number"
              placeholder="Commission %"
              value={cc.commissionValue}
              onChange={(e) =>
                updateBlock(index, "commissionValue", e.target.value)
              }
              className="border p-2 rounded w-full mt-2"
              required
            />

            {(cc.commissionType !== "all_products") && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-3">
                {products.map((p) => (
                  <label key={p.id} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={cc.selectedProducts.includes(p.id)}
                      onChange={() => toggleProduct(index, p.id)}
                    />
                    {p.name}
                  </label>
                ))}
              </div>
            )}

            {form.customerCommissions.length > 1 && (
              <button
                type="button"
                onClick={() => removeBlock(index)}
                className="text-red-600 text-sm mt-2"
              >
                Remove Customer
              </button>
            )}
          </div>
        ))}

        <button
          type="button"
          onClick={addBlock}
          className="px-3 py-2 rounded bg-blue-600 text-white"
        >
          + Link Another Customer
        </button>

        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={() => navigate("/doctors")}
            className="border px-6 py-2 rounded"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="bg-green-600 text-white px-6 py-2 rounded"
          >
            {saving ? "Saving..." : "Save Doctor"}
          </button>
        </div>
      </form>

      {loading && <div className="text-center mt-4">Loading…</div>}
    </div>
  );
};

export default DoctorForm;
